/**
 * @file contract.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-23
 * @description 键盘性能参数功能契约
 *
 * 键盘通过 HID EEPROM 协议读写回报率/休眠时间等性能参数。
 * 当前只支持基础配置，后续可按需扩展。
 *
 * GCOME_AP 特有扩展（Tick/防抖/Lock/触底稳定/自适应校准/WASD 切换）也在契约中声明，
 * 其他芯片默认 NOT_SUPPORTED；扩展类型见 types/keyboardPerfCfgGcomeAp.ts。
 */

import { KeyboardPerfCfg, SleepTime, KeyboardReportRate } from '../../types/keyboardPerCfg'
import {
  GcomeApPerfStatus,
  GcomeApConnectionMode,
  WasdSwapReport,
} from '../../types/keyboardPerfCfgGcomeAp'
import { FeatureBase } from '../FeatureBase'

export * from './hostHooks'

// 扩展功能注册表
declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    keyboardPerfCfg: IKeyboardPerfCfgFeature
  }
}

export interface IKeyboardPerfCfgFeature {
  // ==== 基础接口 ====
  // 获取键盘回报率码（芯片自有语义联合 KeyboardReportRate：585 系列 EEPROM 码 / GCOME_AP EP 码，不做转码）
  getReportRate(): Promise<KeyboardReportRate>
  // 设置键盘回报率码（与 getReportRate 同语义）
  setReportRate(rate: KeyboardReportRate): Promise<void>
  // 获取休眠时间
  getSleepTime(): Promise<SleepTime>
  // 设置休眠时间
  setSleepTime(time: SleepTime): Promise<void>

  // 获取所有配置（GCOME_AP 返回 GcomeApPerfStatus 扩展字段）
  getAllPerfCfg(): Promise<KeyboardPerfCfg | GcomeApPerfStatus>
  // 设置所有配置（GCOME_AP 可写 Tick/Lock/触底稳定等扩展字段）
  setAllPerfCfg(cfg: KeyboardPerfCfg | GcomeApPerfStatus): Promise<void>

  // ==== GCOME_AP 特有扩展（其他芯片默认 NOT_SUPPORTED）====
  // 获取 tick 速率（0=无, 1=30tick, 2=60tick）
  getTickRate(): Promise<number>
  // 设置 tick 速率（0/1/2）
  setTickRate(rate: number): Promise<void>
  // 获取防抖等级（0-7）
  getDebounceLevel(): Promise<number>
  // 设置防抖等级（0-7）
  setDebounceLevel(level: number): Promise<void>
  // 获取 Lock mask（字节6：Bit0 锁Win / Bit1 锁ALT+TAB / Bit2 锁ALT+F4 / Bit3 APP）
  getLockMask(): Promise<number>
  // 设置 Lock mask
  setLockMask(mask: number): Promise<void>
  // 锁定 Win 键
  getLockWin(): Promise<boolean>
  setLockWin(enabled: boolean): Promise<void>
  // 锁定 ALT+TAB
  getLockAltTab(): Promise<boolean>
  setLockAltTab(enabled: boolean): Promise<void>
  // 锁定 ALT+F4
  getLockAltF4(): Promise<boolean>
  setLockAltF4(enabled: boolean): Promise<void>
  // 触底时始终触发（触底稳定）
  getTouchBottomTrigger(): Promise<boolean>
  setTouchBottomTrigger(enabled: boolean): Promise<void>
  // 智能动态校准（自适应动态校准2.0）
  getAdaptiveCalibration(): Promise<boolean>
  setAdaptiveCalibration(enabled: boolean): Promise<void>
  // 系统模式（字节1 Bit1）：true=Mac 模式，false=Win 模式
  getMacMode(): Promise<boolean>
  setMacMode(enabled: boolean): Promise<void>
  // 全键无冲（功能变量区字节33）
  getFullKeyRollover(): Promise<boolean>
  setFullKeyRollover(enabled: boolean): Promise<void>
  // 连接模式（字节36）：0=2.4G, 1-3=蓝牙1-3通道, 0x0f=有线
  getConnectionMode(): Promise<GcomeApConnectionMode>
  // WASD 与方向键互换（改键层面，GCOME_AP 全量读见 GcomeApPerfStatus.swapWasd）
  getSwapWasd(): Promise<boolean>
  // 设置 WASD 与方向键互换，返回写后读回确认报告（诊断用）
  setSwapWasd(enabled: boolean): Promise<WasdSwapReport>
}

export class keyboardPerfCfgFeature extends FeatureBase implements IKeyboardPerfCfgFeature {
  getReportRate(): Promise<KeyboardReportRate> {
    return this.unsupported('keyboardPerfCfgFeature.getReportRate')
  }
  setReportRate(_rate: KeyboardReportRate): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setReportRate')
  }
  getSleepTime(): Promise<SleepTime> {
    return this.unsupported('keyboardPerfCfgFeature.getSleepTime')
  }
  setSleepTime(_time: SleepTime): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setSleepTime')
  }
  getAllPerfCfg(): Promise<KeyboardPerfCfg | GcomeApPerfStatus> {
    return this.unsupported('keyboardPerfCfgFeature.getAllPerfCfg')
  }
  setAllPerfCfg(_cfg: KeyboardPerfCfg | GcomeApPerfStatus): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setAllPerfCfg')
  }

  getTickRate(): Promise<number> {
    return this.unsupported('keyboardPerfCfgFeature.getTickRate')
  }
  setTickRate(_rate: number): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setTickRate')
  }
  getDebounceLevel(): Promise<number> {
    return this.unsupported('keyboardPerfCfgFeature.getDebounceLevel')
  }
  setDebounceLevel(_level: number): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setDebounceLevel')
  }
  getLockMask(): Promise<number> {
    return this.unsupported('keyboardPerfCfgFeature.getLockMask')
  }
  setLockMask(_mask: number): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setLockMask')
  }
  getLockWin(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getLockWin')
  }
  setLockWin(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setLockWin')
  }
  getLockAltTab(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getLockAltTab')
  }
  setLockAltTab(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setLockAltTab')
  }
  getLockAltF4(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getLockAltF4')
  }
  setLockAltF4(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setLockAltF4')
  }
  getTouchBottomTrigger(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getTouchBottomTrigger')
  }
  setTouchBottomTrigger(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setTouchBottomTrigger')
  }
  getAdaptiveCalibration(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getAdaptiveCalibration')
  }
  setAdaptiveCalibration(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setAdaptiveCalibration')
  }
  getMacMode(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getMacMode')
  }
  setMacMode(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setMacMode')
  }
  getFullKeyRollover(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getFullKeyRollover')
  }
  setFullKeyRollover(_enabled: boolean): Promise<void> {
    return this.unsupported('keyboardPerfCfgFeature.setFullKeyRollover')
  }
  getConnectionMode(): Promise<GcomeApConnectionMode> {
    return this.unsupported('keyboardPerfCfgFeature.getConnectionMode')
  }
  getSwapWasd(): Promise<boolean> {
    return this.unsupported('keyboardPerfCfgFeature.getSwapWasd')
  }
  setSwapWasd(_enabled: boolean): Promise<WasdSwapReport> {
    return this.unsupported('keyboardPerfCfgFeature.setSwapWasd')
  }
}
